import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NgStrapiAuthConfig } from '../../../../../services/strapi/auth/ng-strapi-auth-config';
import { Moment } from 'moment/moment';

// Define the shape of the data you expect to receive
export interface ShareDialogData {
    venueId: string;
    venueName: string;
    dateStart: Moment;
    dateEnd: Moment;
}

@Component({
    selector: 'app-share-dialog',
    templateUrl: './share-dialog.html',
    styleUrls: ['./share-dialog.scss']
})
export class ShareDialogComponent {
    isLoading = false;
    isCopied = false;
    generatedLink: string | null = null;
    errorMessage: string | null = null; // New property for error messages
    private apiUrl: string;

    constructor(
        public dialogRef: MatDialogRef<ShareDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ShareDialogData,
        private http: HttpClient,
        @Inject('config') private config: NgStrapiAuthConfig,
    ) {
        if (this.config && this.config.apiUrl) {
            this.apiUrl = this.config.apiUrl;
        } else {
            const err = '[NgStrapiAuth]: no api url provided';
            console.error(err);
            throw new Error(err);
        }
    }

    onClose(): void {
        this.dialogRef.close();
    }

    async onGenerateLink(): Promise<void> {
        this.isLoading = true;
        this.errorMessage = null; // Clear previous errors on a new attempt

        try {
            const result: any = await this.http
                .post(this.apiUrl + '/venues/createSharedPage', {
                    'data': {'venue_ids': [this.data.venueId],
                        'start_range': this.data.dateStart.format('YYYY-MM-DD'),
                        'end_range': this.data.dateEnd.format('YYYY-MM-DD')}
                })
                .toPromise();
            const {status, unique_code} = result;
            const origin = window.location.origin;
            if (status === 'success') {
                this.generatedLink = `${origin}/share/overview?code=${unique_code}`;
            } else {
                // Handle cases where the backend call succeeds but returns an unexpected payload
                throw new Error('Invalid response from server.');
            }
        } catch (error) {
            console.error('Error generating link:', error);
            if (error instanceof HttpErrorResponse) {
                // You can provide more specific errors based on the response
                this.errorMessage = `Failed to generate link. Server responded with status ${error}.`;
            } else {
                this.errorMessage = 'An unexpected error occurred. Please try again.';
            }
        } finally {
            // This block will always run, ensuring isLoading is turned off
            this.isLoading = false;
        }
    }

    onCopyLink(): void {
        if (!this.generatedLink) {
            return;
        }

        // Try modern Clipboard API first
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(this.generatedLink).then(() => {
                this.isCopied = true;
                setTimeout(() => { this.isCopied = false; }, 2000);
            }).catch(err => {
                this.fallbackCopyText(this.generatedLink);
            });
        } else {
            // Fallback for iOS and unsupported browsers
            this.fallbackCopyText(this.generatedLink);
        }
    }

    private fallbackCopyText(text: string) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        // Avoid scrolling to bottom
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        try {
            const successful = document.execCommand('copy');
            this.isCopied = successful;
            setTimeout(() => { this.isCopied = false; }, 2000);
        } catch (err) {
            // Optionally show a message to the user
            alert('Copy not supported on this device. Please copy the link manually.');
        }

        document.body.removeChild(textarea);
    }
}
